import { useState } from 'react';
import styles from './ApiTemplatesModal.module.css';

const copyToClipboard = (text) => {
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text).catch(() => {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    });
  } else {
    const ta = document.createElement('textarea');
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
  }
};

const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    copyToClipboard(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button className={styles.copyBtn} onClick={handleCopy}>
      {copied ? 'Copied!' : 'Copy curl'}
    </button>
  );
};

const DRCARE_CURL = `curl -X POST 'https://stageapi.drcare247.com/api/v1/Schedule/createappointment' \\
  -H 'Content-Type: application/json' \\
  -H 'AppId: <DRCARE_APP_ID>' \\
  -H 'AppSecret: <DRCARE_APP_SECRET>' \\
  -H 'Origin: <DRCARE_ORIGIN>' \\
  -H 'Authorization: Bearer <AUTH_TOKEN>' \\
  -d '<JSON_BODY>'`;

const DRCARE_BODY = {
  "memberId": "<drcare-member-uuid>  *REQUIRED*",
  "providerId": null,
  "residingState": "FL  *REQUIRED* (2-letter state code)",
  "phone": "5551234567  *REQUIRED* (10-digit US, no dashes)",
  "methodId": 3,
  "modeId": 3,
  "durationId": 1,
  "consultationTypeId": 1,
  "scheduleOnUTC": "2026-03-16T12:00:00Z  *REQUIRED* (ISO-8601)",
  "statusId": 1,
  "specialityId": "<per-product>  *REQUIRED*",
  "reasonId": "<per-product>  *REQUIRED*",
  "serviceId": null,
  "note": "Medication request for Product Name (optional)",
  "vital": {
    "height": "5'10\"  (format: X'Y\")",
    "weight": "170  (string)",
    "takenOnUTC": "2026-03-16T12:00:00Z  *REQUIRED* (ISO-8601)"
  },
  "products": [
    {
      "code": "BLM00xxx  *REQUIRED* (DCC/NDC code)",
      "quantity": 1,
      "codeType": "DCC"
    }
  ],
  "questionnaires": [
    {
      "question": "Do you have allergies?  *REQUIRED*",
      "answer": "No known allergies"
    }
  ],
  "Consent": [
    {
      "Title": "Telehealth Consent  *REQUIRED*",
      "Content": "I consent to telehealth services...",
      "IsAgreed": true,
      "agreedOn": "2026-03-16T12:00:00Z  *REQUIRED* (ISO-8601)"
    }
  ]
};

const DRCARE_SUCCESS = {
  "status": 1,
  "data": { "id": "uuid-appointment-id" },
  "messages": []
};

const DRCARE_ERROR = {
  "status": 0,
  "data": null,
  "messages": [{ "text": "Error description from DrCare" }]
};

const AUTHNET_CURL = `curl -X POST 'https://api.authorize.net/xml/v1/request.api' \\
  -H 'Content-Type: application/json' \\
  -d '<JSON_BODY>'

# Sandbox: https://apitest.authorize.net/xml/v1/request.api
# Production: https://api.authorize.net/xml/v1/request.api`;

const AUTHNET_HOLD_BODY = {
  "createTransactionRequest": {
    "merchantAuthentication": {
      "name": "<API_LOGIN_ID>  *REQUIRED*",
      "transactionKey": "<TRANSACTION_KEY>  *REQUIRED*"
    },
    "transactionRequest": {
      "transactionType": "authOnlyTransaction",
      "amount": "69.99  *REQUIRED* (dollars, 2 decimals)",
      "payment": {
        "creditCard": {
          "cardNumber": "4111111111111111  *REQUIRED*",
          "expirationDate": "1228  *REQUIRED* (MMYY format)",
          "cardCode": "123  *REQUIRED* (CVV)"
        }
      },
      "order": {
        "invoiceNumber": "OV-8927  (optional, max 20 chars)",
        "description": "OVAL Order OV-8927  (optional)"
      },
      "billTo": {
        "firstName": "John  (optional)",
        "lastName": "Doe  (optional)",
        "address": "123 Main St  (optional)",
        "city": "Miami  (optional)",
        "state": "FL  (optional)",
        "zip": "33101  (optional)",
        "country": "US  (optional)"
      }
    }
  }
};

const AUTHNET_HOLD_SUCCESS = {
  "transactionResponse": {
    "responseCode": "1",
    "authCode": "HH5414",
    "avsResultCode": "Y",
    "cvvResultCode": "M",
    "transId": "60019493795",
    "accountNumber": "XXXX1111",
    "accountType": "Visa",
    "messages": [{ "code": "1", "description": "This transaction has been approved." }]
  },
  "messages": { "resultCode": "Ok", "message": [{ "code": "I00001", "text": "Successful." }] }
};

const AUTHNET_HOLD_ERROR = {
  "transactionResponse": {
    "responseCode": "2",
    "errors": [{ "errorCode": "2", "errorText": "This transaction has been declined." }],
    "transId": "0",
    "accountNumber": "XXXX1111",
    "accountType": "Visa"
  },
  "messages": { "resultCode": "Error", "message": [{ "code": "E00027", "text": "The transaction was unsuccessful." }] }
};

const AUTHNET_CAPTURE_CURL = `curl -X POST 'https://api.authorize.net/xml/v1/request.api' \\
  -H 'Content-Type: application/json' \\
  -d '<JSON_BODY>'`;

const AUTHNET_CAPTURE_BODY = {
  "createTransactionRequest": {
    "merchantAuthentication": {
      "name": "<API_LOGIN_ID>  *REQUIRED*",
      "transactionKey": "<TRANSACTION_KEY>  *REQUIRED*"
    },
    "transactionRequest": {
      "transactionType": "priorAuthCaptureTransaction",
      "amount": "69.99  *REQUIRED* (must match or be less than auth amount)",
      "refTransId": "60019493795  *REQUIRED* (original auth transaction ID)"
    }
  }
};

const AUTHNET_CAPTURE_SUCCESS = {
  "transactionResponse": {
    "responseCode": "1",
    "authCode": "HH5414",
    "transId": "60019493796",
    "messages": [{ "code": "1", "description": "This transaction has been approved." }]
  },
  "messages": { "resultCode": "Ok", "message": [{ "code": "I00001", "text": "Successful." }] }
};

const AUTHNET_CAPTURE_ERROR = {
  "transactionResponse": {
    "responseCode": "3",
    "errors": [{ "errorCode": "16", "errorText": "The transaction cannot be found." }],
    "transId": "0"
  },
  "messages": { "resultCode": "Error", "message": [{ "code": "E00027", "text": "The transaction was unsuccessful." }] }
};

const JsonBlock = ({ data, label }) => (
  <div className={styles.jsonBlock}>
    {label && <div className={styles.jsonLabel}>{label}</div>}
    <pre className={styles.jsonPre}>{JSON.stringify(data, null, 2)}</pre>
  </div>
);

const Section = ({ title, curlCmd, body, successResp, errorResp, notes }) => (
  <div className={styles.section}>
    <h3 className={styles.sectionTitle}>{title}</h3>
    {notes && <p className={styles.sectionNotes}>{notes}</p>}
    <div className={styles.curlBlock}>
      <div className={styles.curlHeader}>
        <span className={styles.curlLabel}>curl command</span>
        <CopyButton text={curlCmd} />
      </div>
      <pre className={styles.curlPre}>{curlCmd}</pre>
    </div>
    <JsonBlock data={body} label="Request Body" />
    <div className={styles.responseRow}>
      <JsonBlock data={successResp} label="Success Response" />
      <JsonBlock data={errorResp} label="Error Response" />
    </div>
  </div>
);

const ApiTemplatesModal = ({ onClose }) => (
  <div className={styles.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
    <div className={styles.modal}>
      <div className={styles.modalHeader}>
        <h2>API Templates</h2>
        <button className={styles.closeBtn} onClick={onClose}>×</button>
      </div>
      <div className={styles.modalBody}>
        <Section
          title="1. DrCare — Create Appointment"
          notes="Submits a telehealth consultation request. Fields marked *REQUIRED* must be provided. Fixed values: methodId=3, modeId=3, durationId=1, consultationTypeId=1, statusId=1, codeType='DCC'. specialityId and reasonId are per-product (from products table)."
          curlCmd={DRCARE_CURL}
          body={DRCARE_BODY}
          successResp={DRCARE_SUCCESS}
          errorResp={DRCARE_ERROR}
        />
        <Section
          title="2. Authorize.Net — Auth-Only Hold"
          notes="Creates a payment authorization without capturing funds. Amount is in dollars (not cents). Card expiration in MMYY format. The hold expires after ~7 days if not captured or voided."
          curlCmd={AUTHNET_CURL}
          body={AUTHNET_HOLD_BODY}
          successResp={AUTHNET_HOLD_SUCCESS}
          errorResp={AUTHNET_HOLD_ERROR}
        />
        <Section
          title="3. Authorize.Net — Capture (priorAuthCapture)"
          notes="Captures a previously authorized transaction. Amount must match or be less than the original auth amount. refTransId is the transId from the original authOnlyTransaction response."
          curlCmd={AUTHNET_CAPTURE_CURL}
          body={AUTHNET_CAPTURE_BODY}
          successResp={AUTHNET_CAPTURE_SUCCESS}
          errorResp={AUTHNET_CAPTURE_ERROR}
        />
      </div>
    </div>
  </div>
);

export default ApiTemplatesModal;
